import { ImageSquareIcon, XIcon } from '@phosphor-icons/react'
import type { KeyboardEvent, MouseEvent } from 'react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { BorderLoader } from '@/components/ui/loaders/border-loader'
import { Typography } from '@/components/ui/typography'
// Import directly (not via the `@/utils/file` barrel) so this component doesn't transitively pull
// in download-csv.ts's `import.meta.url` Worker usage, which Jest's Babel transform can't parse.
import { getFileIcon } from '@/utils/file/get-file-icon'
import { getFileType } from '@/utils/file/get-file-type'

import * as S from './file-item.styled'
import { FileItemProps } from './file-item.types'

export const FileItem = ({
  fileName,
  fileType,
  isLoading = false,
  previewUrl,
  size = 'medium',
  onClose,
  onClick,
  ...props
}: FileItemProps) => {
  const fileIcon = getFileIcon(fileType ?? getFileType(fileName))
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const isInteractive = !!onClick && !isLoading

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isInteractive) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick?.(event as unknown as MouseEvent<HTMLDivElement>)
    }
  }

  return (
    <S.Container
      $isLoading={isLoading}
      $isClickable={!!onClick}
      $size={size}
      aria-disabled={onClick && isLoading ? true : undefined}
      aria-label={onClick ? fileName : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      {...props}
    >
      <S.IconWrapper $size={size}>
        {isLoading ? (
          <BorderLoader size={size === 'large' ? 'large' : 'small'} align="center" />
        ) : previewUrl ? (
          <>
            {!isImageLoaded && (
              <Icon
                icon={ImageSquareIcon}
                size={size === 'large' ? '4rem' : '2rem'}
                color="blue500"
              />
            )}
            <S.PreviewImg
              src={previewUrl}
              alt={fileName}
              $isVisible={isImageLoaded}
              onLoad={() => setIsImageLoaded(true)}
            />
          </>
        ) : (
          <Icon icon={fileIcon} size={size === 'large' ? '4rem' : '2.4rem'} color="blue500" />
        )}
      </S.IconWrapper>
      <S.TextWrapper>
        <Typography color="black800" size="heading-tiny" breakLines={1}>
          {fileName}
        </Typography>
        {fileType && (
          <Typography color="black800" size="text-medium">
            {fileType}
          </Typography>
        )}
      </S.TextWrapper>
      {onClose && (
        <S.CloseButtonWrap>
          <Button
            onlyBox
            use="ghost"
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
          >
            <Icon icon={XIcon} size="1.6rem" />
          </Button>
        </S.CloseButtonWrap>
      )}
    </S.Container>
  )
}
